import React from 'react';
import { useStatusColor } from '../../hooks/useStatusColor';
import { getStatusChipStyle } from '../../services/statusLookup';

/** Applied to table rows when checked/selected in allocation and shipment grids. */
export const SELECTED_ROW_CLASS = 'data-grid-row-selected';

export const selectedRowClassName = (isSelected, baseClass = '') =>
  (isSelected ? `${SELECTED_ROW_CLASS} ${baseClass}` : baseClass).trim();

/** Truncated table cell text with full value in tooltip. */
export function GridText({ value, className = '', title }) {
  const text = value == null || value === '' ? '-' : String(value);
  return (
    <span className={`block truncate ${className}`} title={title ?? text}>
      {text}
    </span>
  );
}

/** Multi-line clamp for addresses / remarks. */
export function GridClamp({ value, lines = 2, className = '' }) {
  const text = value == null || value === '' ? '-' : String(value);
  const clampClass = lines === 2 ? 'line-clamp-2' : `line-clamp-${lines}`;
  return (
    <span className={`block ${clampClass} cursor-help ${className}`} title={text}>
      {text}
    </span>
  );
}

/**
 * Order/status badge. Color comes from prop or StatusInDropDown (by Id / Name).
 * @param {'solid'|'soft'} variant - solid gradient (grids) or soft tint (lists)
 */
export function StatusPill({ name, color, status, statusId, variant = 'solid' }) {
  const label = name ?? status?.Name;
  const resolvedColor = useStatusColor({
    color: color ?? status?.Color,
    name: label,
    statusId: statusId ?? status?.Id,
    status,
  });

  if (!label) return <GridText value="-" />;

  if (variant === 'soft') {
    return (
      <span
        className="inline-flex max-w-full items-center overflow-hidden rounded-full px-2.5 py-0.5 text-xs font-medium"
        style={getStatusChipStyle(resolvedColor)}
        title={label}
      >
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex max-w-full items-center overflow-hidden rounded-full px-3 py-1.5 text-xs font-semibold text-white"
      style={{
        background: resolvedColor
          ? `linear-gradient(to right, ${resolvedColor} 0%, ${resolvedColor} 45%, ${resolvedColor}cc 50%, ${resolvedColor}aa 100%)`
          : 'linear-gradient(to right, #6B7280 0%, #6B7280 45%, #9CA3AF 50%, #D1D5DB 100%)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
      }}
      title={label}
    >
      <span className="truncate">{label}</span>
    </span>
  );
}
